import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { priceBookItemsApi, priceBookAssembliesApi, quoteLinesApi } from '../api';
import { centsToDollarString, dollarsToCents } from '../constants';
import { UNIT_TYPE_OPTIONS, COST_CLASS_OPTIONS, unitAbbr, pricingStatusLabel, PRICING_STATUS_STYLES } from '../priceBookConstants';
import { CategorySelect } from './CategorySelect';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls = 'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const labelCls = 'text-xs text-gray-400 mb-1 block';
const price = (c) => (c == null ? 'Not set' : centsToDollarString(c));

const err = (e, fallback) => {
  const d = e.response?.data?.detail;
  toast.error(typeof d === 'string' ? d : Array.isArray(d) ? d[0]?.msg || fallback : fallback);
};

const ItemsTab = ({ quote, onAdded }) => {
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState('1');
  const [section, setSection] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const p = {};
      if (search.trim()) p.search = search.trim();
      if (cat) p.category = cat;
      priceBookItemsApi.list(p).then((r) => setItems(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search, cat]);

  const add = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await quoteLinesApi.fromPriceBook(quote.id, {
        item_id: selected.id, quantity_decimal: qty, price_level: 'standard',
        section_name: section.trim() || null,
      });
      toast.success(`Added ${selected.name}`);
      setSelected(null); setQty('1');
      onAdded();
    } catch (e) { err(e, 'Could not add item'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="add-work-items-tab">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-3 text-gray-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Price Book items…" className={`${inputCls} pl-8`} data-testid="item-picker-search" />
        </div>
        <CategorySelect value={cat} onChange={setCat} emptyLabel="All categories" className="w-40 shrink-0" data-testid="item-picker-category" />
      </div>
      <div className="max-h-52 overflow-y-auto space-y-1 border border-gray-800 rounded p-2">
        {items.length === 0 && <p className="text-sm text-gray-500 p-2">No active items found.</p>}
        {items.map((it) => (
          <button key={it.id} onClick={() => setSelected(it)}
            className={`w-full text-left p-2 rounded text-sm ${selected?.id === it.id ? 'bg-[#D4AF37]/15 border border-[#D4AF37]/40' : 'hover:bg-[#1A1A1A] border border-transparent'}`}
            data-testid={`item-picker-row-${it.id}`}>
            <div className="flex justify-between items-center gap-2">
              <span className="text-white">{it.name}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${PRICING_STATUS_STYLES[it.pricing_status] || PRICING_STATUS_STYLES.missing_standard_price}`}>{pricingStatusLabel(it.pricing_status)}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {it.category ? `${it.category} · ` : ''}{unitAbbr(it)} · Price {price(it.standard_unit_price_cents)}
            </div>
          </button>
        ))}
      </div>
      {selected && (
        <div className="border border-gray-800 rounded p-3 space-y-3 bg-[#0D0D0D]" data-testid="item-picker-config">
          <div className="text-sm text-white font-medium">{selected.name} <span className="text-gray-500 font-normal">— {price(selected.standard_unit_price_cents)} / {unitAbbr(selected)}</span></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Quantity *</label><Input value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} data-testid="item-picker-qty" /></div>
            <div><label className={labelCls}>Section</label><Input value={section} onChange={(e) => setSection(e.target.value)} placeholder="Optional" className={inputCls} data-testid="item-picker-section" /></div>
          </div>
          <Button onClick={add} disabled={busy} className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="item-picker-add-btn">
            {busy && <Loader2 size={14} className="animate-spin" />} Add to Quote
          </Button>
        </div>
      )}
    </div>
  );
};

const AssembliesTab = ({ quote, onAdded }) => {
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('');
  const [assemblies, setAssemblies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState('1');
  const [section, setSection] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const p = {};
      if (search.trim()) p.search = search.trim();
      if (cat) p.category = cat;
      priceBookAssembliesApi.list(p).then((r) => setAssemblies(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search, cat]);

  const insert = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await quoteLinesApi.fromAssembly(quote.id, {
        assembly_id: selected.id, primary_quantity_decimal: qty, price_level: 'standard',
        section_name: section.trim() || null,
      });
      toast.success(`Inserted ${selected.name} (${r.data.inserted_count} lines)`);
      setSelected(null); setQty('1');
      onAdded();
    } catch (e) { err(e, 'Could not insert assembly'); } finally { setBusy(false); }
  };

  const preview = (a, key, missKey) => (a.preview[missKey] > 0 ? 'Incomplete' : centsToDollarString(a.preview[key]));

  return (
    <div className="space-y-3" data-testid="add-work-assemblies-tab">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-3 text-gray-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search assemblies…" className={`${inputCls} pl-8`} data-testid="assembly-picker-search" />
        </div>
        <CategorySelect value={cat} onChange={setCat} emptyLabel="All categories" className="w-40 shrink-0" data-testid="assembly-picker-category" />
      </div>
      <div className="max-h-52 overflow-y-auto space-y-1 border border-gray-800 rounded p-2">
        {assemblies.length === 0 && <p className="text-sm text-gray-500 p-2">No active assemblies found.</p>}
        {assemblies.map((a) => (
          <button key={a.id} onClick={() => setSelected(a)}
            className={`w-full text-left p-2 rounded text-sm ${selected?.id === a.id ? 'bg-[#D4AF37]/15 border border-[#D4AF37]/40' : 'hover:bg-[#1A1A1A] border border-transparent'}`}
            data-testid={`assembly-picker-row-${a.id}`}>
            <div className="flex justify-between items-center gap-2">
              <span className="text-white">{a.name}</span>
              {a.preview.pricing_incomplete && <span className="text-[9px] px-1.5 py-0.5 rounded-full border bg-amber-900/30 text-amber-400 border-amber-800 whitespace-nowrap">Pricing Incomplete</span>}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {a.preview.component_count} components · Price {preview(a, 'standard_price_preview_cents', 'missing_standard_price_component_count')}
            </div>
          </button>
        ))}
      </div>
      {selected && (
        <div className="border border-gray-800 rounded p-3 space-y-3 bg-[#0D0D0D]" data-testid="assembly-picker-config">
          <div className="text-sm text-white font-medium">{selected.name}</div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Primary Quantity *</label><Input value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} data-testid="assembly-picker-qty" /></div>
            <div><label className={labelCls}>Section</label><Input value={section} onChange={(e) => setSection(e.target.value)} placeholder="Optional" className={inputCls} data-testid="assembly-picker-section" /></div>
          </div>
          <Button onClick={insert} disabled={busy} className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="assembly-picker-insert-btn">
            {busy && <Loader2 size={14} className="animate-spin" />} Insert Assembly
          </Button>
        </div>
      )}
    </div>
  );
};

const CustomTab = ({ quote, onAdded }) => {
  const [form, setForm] = useState({ name: '', description: '', section_name: '', category: '', cost_classification: '', unit_type: 'each', custom_unit_label: '', quantity_decimal: '1', customer_price: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async () => {
    if (!form.name.trim()) return toast.error('Line name is required');
    setBusy(true);
    try {
      await quoteLinesApi.createCustom(quote.id, {
        name: form.name, description: form.description || null, section_name: form.section_name || null,
        category: form.category || null, cost_classification: form.cost_classification || null,
        unit_type: form.unit_type, custom_unit_label: form.custom_unit_label || null,
        quantity_decimal: form.quantity_decimal,
        customer_unit_price_cents: dollarsToCents(form.customer_price),
      });
      toast.success('Custom line added');
      setForm((f) => ({ ...f, name: '', description: '', customer_price: '', quantity_decimal: '1' }));
      onAdded();
    } catch (e) { err(e, 'Could not add custom line'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="add-work-custom-tab">
      <div><label className={labelCls}>Name *</label><Input value={form.name} onChange={set('name')} className={inputCls} data-testid="custom-line-name" /></div>
      <div><label className={labelCls}>Description</label><Textarea value={form.description} onChange={set('description')} rows={2} className={inputCls} data-testid="custom-line-description" /></div>
      <div className="grid grid-cols-3 gap-3">
        <div><label className={labelCls}>Quantity *</label><Input value={form.quantity_decimal} onChange={set('quantity_decimal')} className={inputCls} data-testid="custom-line-qty" /></div>
        <div>
          <label className={labelCls}>Unit *</label>
          <select value={form.unit_type} onChange={set('unit_type')} className={selectCls} data-testid="custom-line-unit">
            {UNIT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {form.unit_type === 'other' ? (
          <div><label className={labelCls}>Custom Unit</label><Input value={form.custom_unit_label} onChange={set('custom_unit_label')} className={inputCls} data-testid="custom-line-unit-label" /></div>
        ) : (
          <div><label className={labelCls}>Section</label><Input value={form.section_name} onChange={set('section_name')} placeholder="Optional" className={inputCls} data-testid="custom-line-section" /></div>
        )}
      </div>
      <div><label className={labelCls}>Customer Unit Price ($)</label><Input value={form.customer_price} onChange={set('customer_price')} placeholder="Leave blank if unknown" className={inputCls} data-testid="custom-line-customer-price" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Category</label><Input value={form.category} onChange={set('category')} className={inputCls} data-testid="custom-line-category" /></div>
        <div>
          <label className={labelCls}>Classification</label>
          <select value={form.cost_classification} onChange={set('cost_classification')} className={selectCls} data-testid="custom-line-class">
            <option value="">None</option>
            {COST_CLASS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <Button onClick={add} disabled={busy} className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="custom-line-add-btn">
        {busy && <Loader2 size={14} className="animate-spin" />} Add Custom Line
      </Button>
    </div>
  );
};

export const AddWorkDialog = ({ open, onOpenChange, quote, onAdded }) => {
  const [tab, setTab] = useState('items');
  useEffect(() => { if (open) setTab('items'); }, [open]);
  if (!quote) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="add-work-dialog">
        <DialogHeader><DialogTitle>Add Work</DialogTitle></DialogHeader>
        <div className="flex gap-1 border-b border-gray-800 pb-px mb-2">
          {[{ k: 'items', l: 'Price Book Items' }, { k: 'assemblies', l: 'Assemblies' }, { k: 'custom', l: 'Custom Line' }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-3 py-2 text-sm border-b-2 transition-colors ${tab === t.k ? 'border-[#D4AF37] text-[#D4AF37] font-medium' : 'border-transparent text-gray-400 hover:text-white'}`}
              data-testid={`add-work-tab-${t.k}`}>{t.l}</button>
          ))}
        </div>
        {tab === 'items' && <ItemsTab quote={quote} onAdded={onAdded} />}
        {tab === 'assemblies' && <AssembliesTab quote={quote} onAdded={onAdded} />}
        {tab === 'custom' && <CustomTab quote={quote} onAdded={onAdded} />}
      </DialogContent>
    </Dialog>
  );
};
